import { ChangeDetectionStrategy, Component, ContentChild, EventEmitter, Input, Output, TemplateRef } from '@angular/core'

import { ProductState } from '../../models/product-state'
import { FacetState } from '../../../facets/models/facet-state'
import { UXPage } from '../../../core/models/ux-page'
import { FacetToggleEvent } from '../../../facets/models/facet-toggle-event'
import { Product } from '../../../core/models/product'
import { UXFacetChip, UXFacetGroup } from '../../../facets/models/ux-facets'
import { AppliedFacets } from '../../../facets/models/applied-facets'
import { AddCartEvent, CartProduct } from '../../../cart/models/cart-models';
import { ThemeConfig, USER_CHOICE } from '../../../core/helpers/theme-config'
import { Collapsable } from '../../models/collapsable';
import { FavoriteProduct, ToggleFavoriteEvent } from '../../models/favorite-product';
import { select, Store } from '@ngrx/store';
import * as preferences from '../../../base/actions/preferences.actions'
import * as fromBase from '../../../base/reducers'
import { Observable } from 'rxjs';
import { MenuItems } from '../../../base/models/menu-items';
import { Setting } from '../../../core/models/setting';
import { CombinedFacets } from '../../../facets/models/facet';
import { mapOrder } from '../../../core/helpers/sorting'
import { FilterToggleEvent, FilterItem, FilterGroup } from '../../../shared/models/filter';
import { Crumb } from '../../../core/models/crumb'
import { SortOrder } from '../../models/sort-order'

const hasChoice = ThemeConfig.get<string>('catalog', 'view-type') === USER_CHOICE


@Component({
  selector: 'app-catalog-page',
  templateUrl: './catalog-page.component.html',
  styleUrls: ['./catalog-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CatalogPageComponent {
  @Input() loading: boolean
  @Input() loaded = false
  @Input() productState: ProductState
  @Input() facetState: FacetState
  @Input() combinedFacets: CombinedFacets
  @Input() cartProducts: {[product: string]: CartProduct}
  @Input() favoritesProducts: FavoriteProduct[]
  @Input() page: UXPage
  @Input() detailsPageSettings: Setting
  @Input() catalogItemPageSettings: Setting
  @Input() catalogPageSettings: Setting
  @Input() searchPageSettings: Setting
  @Input() viewType: string
  @Input() viewTypeAdjustable: boolean
  @Input() sideMenu: Collapsable[]
  @Input() sideMenuIds: string[]
  @Input() menuItems: MenuItems
  @Input() activeChain: string[] = []
  @Input() quantityPicker: string
  @Input() allowOutOfStockOrdering: boolean
  @Input() crumbs: Crumb[];
  @Input() showCrumbs: boolean;
  @Input() catalogSortOrders: SortOrder[]
  @Input() sortOrder: string

  @ContentChild(TemplateRef) templateRef: TemplateRef<any>;

  @Output() expand = new EventEmitter<void>()
  @Output() toggleFacet = new EventEmitter<FacetToggleEvent>()
  @Output() toggleShowMore = new EventEmitter<string>()
  @Output() toggleOnlyStock = new EventEmitter<void>()
  @Output() onDetails = new EventEmitter<string>()
  @Output() onAddCart = new EventEmitter<AddCartEvent>()
  @Output() onFavoriteToggle = new EventEmitter<ToggleFavoriteEvent>()
  @Output() onTypeSwitched = new EventEmitter<string>()
  @Output() filterToggled = new EventEmitter<FilterToggleEvent>()
  @Output() sortOrderToggled = new EventEmitter<string>()

  showFilterList: boolean

  fullWidthView$: Observable<boolean>

  constructor(private store: Store<any>) {
    this.fullWidthView$ = store
      .pipe(
        select(fromBase.isFullWidthViewEnabled)
      )
  }

  get hasViewTypeChoice(): boolean {
    return this.viewTypeAdjustable
  }

  get productsLoading(): boolean {
    return this.productState && this.productState.loading
  }

  get products(): Product[] {
    return this.productState && this.productState.products
  }

  get hasProducts(): boolean {
    return this.productsLoading || !!this.products.length
  }

  get productsExpanding(): boolean {
    return this.productState && this.productState.expanding
  }

  get onlyStock(): boolean {
    return this.productState && this.productState.onlyStock
  }

  get facetsLoading(): boolean {
    return this.facetState && this.facetState.loading
  }

  get facets(): UXFacetGroup[] {
    // Re-sort the array based on order in page facetgroups array
    if (this.facetState && this.facetState.groups && this.facetState.groups.length > 0) {
      if (this.page && this.page.facetgroups && this.page.facetgroups.length > 0) {
        return mapOrder(this.facetState.groups, this.page.facetgroups, 'code')
      }
    }
  }

  get openGroups(): string[] {
    return this.facetState && this.facetState.open
  }

  get chips(): UXFacetChip[] {
    return this.facetState && this.facetState.chips
  }

  get applied(): AppliedFacets {
    return this.facetState && this.facetState.applied
  }

  get hasPageDecoration(): boolean {
    return this.page &&
      ((!!this.page.banner && !!this.page.banner.img)
        || !!this.page.blocks.length
        || !!this.page.introText)
  }

  get totalProductCount(): number {
    return this.productState && this.productState.total
  }

  get hasVideos(): boolean {
    if (this.page && this.page.videos && this.page.videos.length) {
      return true
    }
    return false
  }

  get videoUrl(): string {
    return this.page.videos[0].videoUrl
  }

  get videoQuery(): any {
    return this.page.videos[0].query
  }

  get videoAttr(): any {
    return this.page.videos[0].attr
  }

  get filterGroups(): FilterGroup[] {
    let settings: Setting

    switch (this.page.code) {
      case 'catalog':
        settings = this.catalogPageSettings
        break
      case 'search':
        settings = this.searchPageSettings
        break
      default:
        settings = this.catalogPageSettings
    }

    if (settings && settings.showFilters) {
      return settings.showFilters
    }
  }

  get hasSortOrders(): boolean {
    return this.catalogSortOrders && this.catalogSortOrders.length > 0
  }

  expandProducts() {
    this.expand.emit()
  }

  facetToggled(event: FacetToggleEvent) {
    this.toggleFacet.emit(event)
  }

  showMoreToggled(group: string) {
    this.toggleShowMore.emit(group)
  }

  inStockToggled() {
    this.toggleOnlyStock.emit()
  }

  openDetails(product: string) {
    this.onDetails.emit(product)
  }

  toggleFavorite(event: ToggleFavoriteEvent) {
    this.onFavoriteToggle.emit(event)
  }

  addCart(event: AddCartEvent) {
    this.onAddCart.emit(event)
  }

  switchType(event: string) {
    this.onTypeSwitched.emit(event)
  }

  toggleFullWidthView(value: boolean) {
    this.store.dispatch(new preferences.SetFullWidthView(value))
  }

  toggleFilterList() {
    this.showFilterList = !this.showFilterList;
  }

  onFilterToggled(event: FilterToggleEvent) {
    this.filterToggled.emit(event)
  }

  onSortOrderSelect(event: any) {
    this.sortOrderToggled.emit(event.target.value)
  }
}
